import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const timeline = [
    {
      year: '2010',
      title: 'Foundation in Netherlands',
      description: 'Alpine Precision Molding was founded by European toolmaking experts with a vision to bring traditional craftsmanship into the modern era.',
    },
    {
      year: '2013',
      title: 'First Major Automotive Contract',
      description: 'Secured partnership with leading European automotive manufacturer, establishing our reputation for precision and reliability.',
    },
    {
      year: '2016',
      title: 'US Expansion',
      description: 'Opened our Southern California facility to better serve North American clients and expand our global footprint.',
    },
    {
      year: '2018',
      title: 'ISO 9001:2015 Certification',
      description: 'Achieved ISO certification, demonstrating our commitment to quality management and continuous improvement.',
    },
    {
      year: '2020',
      title: 'AI Integration',
      description: 'Pioneered the integration of AI-powered quality control and predictive maintenance systems in our production processes.',
    },
    {
      year: '2022',
      title: 'Medical Device Approval',
      description: 'Received FDA approval for medical device manufacturing, opening new opportunities in the healthcare sector.',
    },
    {
      year: '2024',
      title: 'Sustainability Initiative',
      description: 'Launched comprehensive carbon neutrality roadmap and circular economy programs.',
    },
    {
      year: '2025',
      title: 'Continued Growth',
      description: 'Expanding capabilities with new 1000-ton press and advanced 3D printing technologies.',
    },
  ];

  const values = [
    {
      icon: 'ri-focus-3-line',
      title: 'Precision',
      description: 'We maintain tolerances down to ±0.001mm, ensuring every part meets exact specifications.',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Innovation',
      description: 'Combining traditional craftsmanship with cutting-edge AI and automation technologies.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality',
      description: 'ISO 9001:2015 certified processes and rigorous quality control at every stage.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Partnership',
      description: 'We work closely with clients from design to delivery, ensuring complete satisfaction.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability',
      description: 'Committed to environmental responsibility through efficient processes and recycling programs.',
    },
    {
      icon: 'ri-global-line',
      title: 'Global Reach',
      description: 'Serving clients worldwide with facilities in Europe and North America.',
    },
  ];

  const team = [
    {
      name: 'Ray',
      title: 'Founder & CEO',
      image: 'https://public.readdy.ai/ai/img_res/7ee38c0c55a61ddbdfeb67f6207c381a.jpg',
      bio: 'With over 25 years in precision molding, Ray founded Alpine to bridge European craftsmanship with global manufacturing needs.',
    },
    {
      name: 'Luis',
      title: 'Chief Technology Officer',
      image: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20male%20technology%20executive%20in%20business%20casual%20attire%20confident%20cto%20portrait%20with%20clean%20simple%20background%20modern%20corporate%20headshot%20of%20engineering%20leader&width=400&height=400&seq=team-luis-001&orientation=squarish',
      bio: 'Luis leads our technology initiatives, integrating AI and automation to enhance precision and efficiency.',
    },
    {
      name: 'Dave',
      title: 'VP of Operations',
      image: 'https://readdy.ai/api/search-image?query=professional%20caucasian%20male%20operations%20executive%20in%20business%20attire%20confident%20vp%20portrait%20with%20clean%20simple%20background%20modern%20corporate%20headshot%20of%20manufacturing%20operations%20leader&width=400&height=400&seq=team-dave-001&orientation=squarish',
      bio: 'Dave oversees our production facilities, ensuring quality standards and on-time delivery for every project.',
    },
    {
      name: 'Ruben Ponce',
      title: 'Internet Promotion & Liaison for World Affairs',
      image: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20male%20marketing%20executive%20in%20modern%20business%20attire%20confident%20digital%20marketing%20leader%20portrait%20with%20clean%20simple%20background%20corporate%20headshot%20of%20international%20communications%20specialist&width=400&height=400&seq=team-ruben-001&orientation=squarish',
      bio: 'Ruben manages our global digital presence and international partnerships, connecting Alpine with clients worldwide.',
    },
  ];

  const stats = [
    { number: '15+', label: 'Years of Excellence' },
    { number: '500+', label: 'Projects Completed' },
    { number: '50+', label: 'Team Members' },
    { number: '20+', label: 'Countries Served' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=alpine%20mountain%20peaks%20with%20modern%20manufacturing%20facility%20in%20foreground%20representing%20precision%20and%20excellence%20clean%20industrial%20architecture%20against%20majestic%20mountain%20backdrop%20symbolizing%20european%20craftsmanship%20and%20innovation&width=1920&height=800&seq=about-hero-bg-001&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-building-line text-primary-300"></i>
              <span className="text-sm font-medium text-primary-300">Our Story</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">
              European Heritage, Global Excellence
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              For over 15 years, Alpine Precision Molding has been delivering world-class injection molding, blow molding, and 3D printing solutions. We combine traditional European craftsmanship with cutting-edge AI technology to serve clients across automotive, medical, consumer goods, and aerospace industries.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-display font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-slate-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">
                Our Mission
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                To deliver precision molding solutions that exceed expectations through the perfect blend of European craftsmanship, advanced technology, and unwavering commitment to quality. We empower our clients to bring their innovative products to market with confidence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">
                Our Vision
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                To be the global leader in precision manufacturing, recognized for innovation, sustainability, and exceptional customer partnerships. We envision a future where traditional craftsmanship and AI-powered automation work in harmony to create perfect products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From a small European workshop to a global manufacturing partner.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-200 hidden lg:block"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-2xl font-display font-bold text-primary-600 mb-2">
                        {item.year}
                      </div>
                      <h3 className="text-xl font-display font-bold text-slate-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Center Dot */}
                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-primary-600 rounded-full border-4 border-white shadow-lg z-10"></div>

                  {/* Spacer */}
                  <div className="hidden lg:block w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Experienced professionals dedicated to excellence and innovation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-slate-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 font-medium text-sm mb-3">
                    {member.title}
                  </p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Our Facilities
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              State-of-the-art manufacturing centers strategically located to serve global markets.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-video overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20manufacturing%20facility%20in%20southern%20california%20with%20advanced%20injection%20molding%20machines%20clean%20bright%20industrial%20workspace%20with%20precision%20equipment%20and%20quality%20control%20stations&width=800&height=450&seq=facility-usa-001&orientation=landscape"
                  alt="Southern California Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-2 text-primary-600 mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                  <h3 className="text-2xl font-display font-bold text-slate-900">
                    Southern California, USA
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our 50,000 sq ft facility features the latest injection molding presses, 3D printing technologies, and AI-powered quality control systems. Serving North American clients with rapid turnaround times.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>15 injection molding presses (50-1000 tons)</span>
                  </li>
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>Industrial 3D printing center</span>
                  </li>
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>Advanced CNC machining department</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-video overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=european%20manufacturing%20facility%20in%20netherlands%20with%20precision%20toolmaking%20equipment%20clean%20modern%20industrial%20workspace%20with%20advanced%20blow%20molding%20machines%20and%20quality%20inspection%20areas&width=800&height=450&seq=facility-europe-001&orientation=landscape"
                  alt="Netherlands Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-2 text-primary-600 mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                  <h3 className="text-2xl font-display font-bold text-slate-900">
                    Netherlands, Europe
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our European headquarters houses our advanced toolmaking division and blow molding operations. Combining traditional craftsmanship with modern automation to serve European and global markets.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>Master toolmaking workshop</span>
                  </li>
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>Blow molding production lines</span>
                  </li>
                  <li className="flex items-center space-x-2 text-sm text-slate-600">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    <span>R&D and prototyping center</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Let's discuss how Alpine Precision Molding can bring your project to life with our expertise and dedication to excellence.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="/contact"
              className="px-8 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-slate-100 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
            <a
              href="/instant-quote"
              className="px-8 py-3 bg-primary-800 text-white font-semibold rounded-lg hover:bg-primary-900 transition-all border border-primary-500 whitespace-nowrap cursor-pointer"
            >
              Get Instant Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
